import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Users, Calendar as CalendarIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import ReserveDateDialog from "@/components/ReserveDateDialog";

// Fixed ordering: standard (workweek) rows first, then specials (weekend)
const SHIFT_ORDER = ["Matí - migdia", "Tarda - nit", "Dia complet", "Matí - tarda"];
const shiftIdx = (name) => {
  const i = SHIFT_ORDER.indexOf(name);
  return i === -1 ? 99 : i;
};

function TariffRows({ rows, onReserve, testid, t }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-[#eae0df] bg-white shadow-[0_10px_35px_rgb(28,36,33,0.05)]" data-testid={testid}>
      {rows.map((tf, i) => (
        <motion.div
          key={tf.id}
          initial={{ opacity: 0, x: -12 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.45, delay: i * 0.05 }}
          data-testid={`tariff-row-${tf.id}`}
          className="grid grid-cols-12 items-center gap-y-2 px-5 md:px-7 py-4 md:py-5 border-b border-[#eae0df] last:border-b-0 hover:bg-[#fdfcf9] transition-colors"
        >
          {/* Day */}
          <div className="col-span-6 md:col-span-3 text-sm md:text-base text-[#1c2421] font-medium" data-testid={`tariff-days-${tf.id}`}>
            {tf.days}
          </div>
          {/* Schedule (desktop) */}
          <div className="hidden md:block md:col-span-2 text-sm text-[#5C544F]" data-testid={`tariff-schedule-${tf.id}`}>
            {tf.schedule}
          </div>
          {/* Shift */}
          <div className="col-span-3 md:col-span-2 text-sm md:text-base text-[#3e664c] italic font-script" data-testid={`tariff-name-${tf.id}`}>
            {tf.name}
          </div>
          {/* Amount */}
          <div className="col-span-3 md:col-span-2 text-right" data-testid={`tariff-price-${tf.id}`}>
            <span className="font-display text-2xl md:text-3xl text-[#3e664c] tracking-tight leading-none">
              {tf.price.toFixed(0)}
              <span className="text-base align-top">€</span>
            </span>
            <span className="block text-[10px] uppercase tracking-[0.22em] text-[#5C544F]/70 mt-1">+ IVA</span>
          </div>
          {/* Reserve button */}
          <div className="col-span-12 md:col-span-3 flex md:justify-end">
            <button
              type="button"
              onClick={() => onReserve(tf)}
              data-testid={`tariff-reserve-btn-${tf.id}`}
              className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-4 md:px-5 py-2.5 rounded-full bg-[#3e664c] hover:bg-[#2e5949] text-[#fdfcf9] text-[11px] uppercase tracking-[0.16em] font-medium transition-colors shrink-0"
            >
              <CalendarIcon size={13} />
              <span className="hidden lg:inline">{t.tariffs.reserveBtnLong}</span>
              <span className="lg:hidden">{t.tariffs.reserveBtn}</span>
            </button>
          </div>
          {/* Mobile-only schedule row */}
          <div className="col-span-12 md:hidden text-xs text-[#5C544F]">
            {tf.schedule}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function TableHeader({ t }) {
  return (
    <div className="grid grid-cols-12 items-center px-5 md:px-7 py-3 md:py-4 bg-[#3e664c] text-[#fdfcf9] rounded-t-2xl">
      <div className="col-span-6 md:col-span-3 uppercase tracking-[0.22em] text-[10px] md:text-xs font-medium">
        {t.tariffs.colDay}
      </div>
      <div className="hidden md:block md:col-span-2 uppercase tracking-[0.22em] text-xs font-medium">
        {t.tariffs.colSchedule}
      </div>
      <div className="col-span-3 md:col-span-2 uppercase tracking-[0.22em] text-[10px] md:text-xs font-medium">
        {t.tariffs.colShift}
      </div>
      <div className="col-span-3 md:col-span-2 uppercase tracking-[0.22em] text-[10px] md:text-xs font-medium text-right">
        {t.tariffs.colAmount}
      </div>
      <div className="hidden md:block md:col-span-3" />
    </div>
  );
}

export default function TariffSection({ whatsappNumber = "34638382033" }) {
  const { t } = useI18n();
  const [tariffs, setTariffs] = useState([]);
  const [selectedTariff, setSelectedTariff] = useState(null);

  useEffect(() => {
    api.get("/tariffs").then((r) => setTariffs(r.data)).catch(() => setTariffs([]));
  }, []);

  const { standard, specials } = useMemo(() => {
    const s = tariffs.filter((tf) => tf.category === "standard").sort((a, b) => shiftIdx(a.name) - shiftIdx(b.name) || a.order - b.order);
    const sp = tariffs.filter((tf) => tf.category === "special").sort((a, b) => (a.order || 0) - (b.order || 0));
    return { standard: s, specials: sp };
  }, [tariffs]);

  return (
    <section
      id="tarifas"
      data-testid="tariffs-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#fdfcf9]"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-14"
        >
          <p className="uppercase tracking-[0.28em] text-xs text-[#3e664c] mb-4 font-medium">
            {t.tariffs.eyebrow}
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.05] uppercase">
            {t.tariffs.titleA}
            <span className="font-script normal-case text-[#5daa92] tracking-tight"> {t.tariffs.titleHl} </span>
            {t.tariffs.titleB}
          </h2>
          <p className="mt-6 text-[#5C544F] leading-relaxed">{t.tariffs.desc}</p>

          <div className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#3e664c] text-[#fdfcf9] text-sm uppercase tracking-[0.18em]" data-testid="tariffs-capacity">
            <Users size={14} />
            {t.tariffs.capacity}
          </div>
        </motion.div>

        {/* Standard block */}
        {standard.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <h3 className="font-display text-2xl md:text-3xl tracking-tight mb-5">
              {t.tariffs.standardTitle}
            </h3>
            <TableHeader t={t} />
            <TariffRows rows={standard} onReserve={setSelectedTariff} testid="tariffs-standard-table" t={t} />
          </motion.div>
        )}

        {/* Specials */}
        {specials.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="font-display text-2xl md:text-3xl tracking-tight mb-5">
              <span className="font-script normal-case text-[#5daa92] mr-3 text-3xl md:text-4xl">{t.tariffs.specialsHl}</span>
              {t.tariffs.specialsTitle}
            </h3>
            <TableHeader t={t} />
            <TariffRows rows={specials} onReserve={setSelectedTariff} testid="tariffs-specials-table" t={t} />
          </motion.div>
        )}

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mt-16"
        >
          <a
            href="#disponibilidad"
            data-testid="tariffs-cta-calendar"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#3e664c] hover:bg-[#2e5949] text-[#fdfcf9] text-sm uppercase tracking-[0.18em] font-medium transition-colors"
          >
            {t.tariffs.cta}
          </a>
          <p className="mt-4 text-xs uppercase tracking-[0.22em] text-[#5C544F]/70">{t.tariffs.footnote}</p>
        </motion.div>
      </div>

      <ReserveDateDialog
        open={!!selectedTariff}
        tariff={selectedTariff}
        whatsappNumber={whatsappNumber}
        onClose={() => setSelectedTariff(null)}
      />
    </section>
  );
}
